/**
 * Shared internationalization module — the single source of truth for
 * translated SEO copy, used by BOTH the server (functions/_middleware.ts,
 * pages.ts) and the client (src/*). It is intentionally dependency-free
 * (no Cloudflare, no React, no DOM) so it bundles cleanly into both builds.
 *
 * URL scheme: English stays at the root (no prefix) to preserve existing
 * indexed URLs; every other language is served under a /{lang}/ prefix, e.g.
 *   /es                              -> Spanish home
 *   /es/trending/united-states/music -> Spanish chart page (slugs stay English)
 *   /es/most-viewed                  -> Spanish all-time records
 *
 * Region/category *slugs* stay English (shared, language-neutral URLs), while
 * the visible names and copy are localized. hreflang alternates then tell
 * Google about every language version of each page.
 *
 * NOTE: pt/fr region prepositions in prose are approximate ("em"/"en") and
 * worth a native review; titles/H1s use a "·" separator to stay grammatical
 * in every language.
 */

export type Lang =
  | 'en' | 'es' | 'pt' | 'de' | 'fr'
  | 'hi' | 'zh' | 'ar' | 'bn' | 'ur' | 'id' | 'ja' | 'ru';

export const DEFAULT_LANG: Lang = 'en';
// English first; order is used for hreflang emission and the language switcher.
// Ordered roughly by number of speakers after English.
export const SUPPORTED_LANGS: Lang[] = [
  'en', 'zh', 'hi', 'es', 'ar', 'bn', 'pt', 'ru', 'ja', 'de', 'fr', 'id', 'ur',
];

export interface LangMeta {
  code: Lang;
  native: string;          // endonym, for the language switcher
  htmlLang: string;        // value for <html lang> / hreflang
  ogLocale: string;        // value for og:locale
  dir?: 'rtl';             // set for right-to-left languages (ar, ur)
}

export const LANGS: Record<Lang, LangMeta> = {
  en: { code: 'en', native: 'English', htmlLang: 'en', ogLocale: 'en_US' },
  es: { code: 'es', native: 'Español', htmlLang: 'es', ogLocale: 'es_ES' },
  pt: { code: 'pt', native: 'Português', htmlLang: 'pt', ogLocale: 'pt_BR' },
  de: { code: 'de', native: 'Deutsch', htmlLang: 'de', ogLocale: 'de_DE' },
  fr: { code: 'fr', native: 'Français', htmlLang: 'fr', ogLocale: 'fr_FR' },
  hi: { code: 'hi', native: 'हिन्दी', htmlLang: 'hi', ogLocale: 'hi_IN' },
  zh: { code: 'zh', native: '中文', htmlLang: 'zh', ogLocale: 'zh_CN' },
  ar: { code: 'ar', native: 'العربية', htmlLang: 'ar', ogLocale: 'ar_AR', dir: 'rtl' },
  bn: { code: 'bn', native: 'বাংলা', htmlLang: 'bn', ogLocale: 'bn_BD' },
  ur: { code: 'ur', native: 'اردو', htmlLang: 'ur', ogLocale: 'ur_PK', dir: 'rtl' },
  id: { code: 'id', native: 'Bahasa Indonesia', htmlLang: 'id', ogLocale: 'id_ID' },
  ja: { code: 'ja', native: '日本語', htmlLang: 'ja', ogLocale: 'ja_JP' },
  ru: { code: 'ru', native: 'Русский', htmlLang: 'ru', ogLocale: 'ru_RU' },
};

export function isLang(x: string | undefined | null): x is Lang {
  return !!x && (SUPPORTED_LANGS as string[]).includes(x);
}

/** Pick the best supported language from an Accept-Language header. */
export function pickLangFromAcceptLanguage(header: string | null): Lang {
  if (!header) return DEFAULT_LANG;
  for (const part of header.split(',')) {
    const code = part.trim().split(';')[0].trim().toLowerCase().slice(0, 2);
    if (isLang(code)) return code;
  }
  return DEFAULT_LANG;
}

// --- Localized region + category display names -----------------------------
// Keyed by YouTube region code / category id. English falls back to the names
// defined in pages.ts, so it is intentionally omitted here.

const REGION_NAMES: Record<Exclude<Lang, 'en'>, Record<string, string>> = {
  es: { ALL: 'todo el mundo', US: 'Estados Unidos', GB: 'Reino Unido', IN: 'India', JP: 'Japón', KR: 'Corea del Sur', BR: 'Brasil', DE: 'Alemania', FR: 'Francia', CA: 'Canadá', AU: 'Australia', MX: 'México', RU: 'Rusia' },
  pt: { ALL: 'todo o mundo', US: 'Estados Unidos', GB: 'Reino Unido', IN: 'Índia', JP: 'Japão', KR: 'Coreia do Sul', BR: 'Brasil', DE: 'Alemanha', FR: 'França', CA: 'Canadá', AU: 'Austrália', MX: 'México', RU: 'Rússia' },
  de: { ALL: 'weltweit', US: 'den USA', GB: 'Großbritannien', IN: 'Indien', JP: 'Japan', KR: 'Südkorea', BR: 'Brasilien', DE: 'Deutschland', FR: 'Frankreich', CA: 'Kanada', AU: 'Australien', MX: 'Mexiko', RU: 'Russland' },
  fr: { ALL: 'le monde', US: 'États-Unis', GB: 'Royaume-Uni', IN: 'Inde', JP: 'Japon', KR: 'Corée du Sud', BR: 'Brésil', DE: 'Allemagne', FR: 'France', CA: 'Canada', AU: 'Australie', MX: 'Mexique', RU: 'Russie' },
  hi: { ALL: 'दुनिया भर', US: 'संयुक्त राज्य', GB: 'यूनाइटेड किंगडम', IN: 'भारत', JP: 'जापान', KR: 'दक्षिण कोरिया', BR: 'ब्राज़ील', DE: 'जर्मनी', FR: 'फ़्रांस', CA: 'कनाडा', AU: 'ऑस्ट्रेलिया', MX: 'मैक्सिको', RU: 'रूस' },
  zh: { ALL: '全球', US: '美国', GB: '英国', IN: '印度', JP: '日本', KR: '韩国', BR: '巴西', DE: '德国', FR: '法国', CA: '加拿大', AU: '澳大利亚', MX: '墨西哥', RU: '俄罗斯' },
  ar: { ALL: 'العالم', US: 'الولايات المتحدة', GB: 'المملكة المتحدة', IN: 'الهند', JP: 'اليابان', KR: 'كوريا الجنوبية', BR: 'البرازيل', DE: 'ألمانيا', FR: 'فرنسا', CA: 'كندا', AU: 'أستراليا', MX: 'المكسيك', RU: 'روسيا' },
  bn: { ALL: 'সারা বিশ্ব', US: 'যুক্তরাষ্ট্র', GB: 'যুক্তরাজ্য', IN: 'ভারত', JP: 'জাপান', KR: 'দক্ষিণ কোরিয়া', BR: 'ব্রাজিল', DE: 'জার্মানি', FR: 'ফ্রান্স', CA: 'কানাডা', AU: 'অস্ট্রেলিয়া', MX: 'মেক্সিকো', RU: 'রাশিয়া' },
  ur: { ALL: 'پوری دنیا', US: 'امریکہ', GB: 'برطانیہ', IN: 'بھارت', JP: 'جاپان', KR: 'جنوبی کوریا', BR: 'برازیل', DE: 'جرمنی', FR: 'فرانس', CA: 'کینیڈا', AU: 'آسٹریلیا', MX: 'میکسیکو', RU: 'روس' },
  id: { ALL: 'seluruh dunia', US: 'Amerika Serikat', GB: 'Britania Raya', IN: 'India', JP: 'Jepang', KR: 'Korea Selatan', BR: 'Brasil', DE: 'Jerman', FR: 'Prancis', CA: 'Kanada', AU: 'Australia', MX: 'Meksiko', RU: 'Rusia' },
  ja: { ALL: '世界', US: 'アメリカ', GB: 'イギリス', IN: 'インド', JP: '日本', KR: '韓国', BR: 'ブラジル', DE: 'ドイツ', FR: 'フランス', CA: 'カナダ', AU: 'オーストラリア', MX: 'メキシコ', RU: 'ロシア' },
  ru: { ALL: 'весь мир', US: 'США', GB: 'Великобритания', IN: 'Индия', JP: 'Япония', KR: 'Южная Корея', BR: 'Бразилия', DE: 'Германия', FR: 'Франция', CA: 'Канада', AU: 'Австралия', MX: 'Мексика', RU: 'Россия' },
};

const CATEGORY_NAMES: Record<Exclude<Lang, 'en'>, Record<string, string>> = {
  es: { ALL: 'Todas las categorías', '10': 'Música', '17': 'Deportes', '20': 'Videojuegos', '24': 'Entretenimiento', '25': 'Noticias' },
  pt: { ALL: 'Todas as categorias', '10': 'Música', '17': 'Esportes', '20': 'Jogos', '24': 'Entretenimento', '25': 'Notícias' },
  de: { ALL: 'Alle Kategorien', '10': 'Musik', '17': 'Sport', '20': 'Gaming', '24': 'Unterhaltung', '25': 'Nachrichten' },
  fr: { ALL: 'Toutes les catégories', '10': 'Musique', '17': 'Sport', '20': 'Jeux vidéo', '24': 'Divertissement', '25': 'Actualités' },
  hi: { ALL: 'सभी श्रेणियाँ', '10': 'संगीत', '17': 'खेल', '20': 'गेमिंग', '24': 'मनोरंजन', '25': 'समाचार' },
  zh: { ALL: '所有类别', '10': '音乐', '17': '体育', '20': '游戏', '24': '娱乐', '25': '新闻' },
  ar: { ALL: 'كل الفئات', '10': 'الموسيقى', '17': 'الرياضة', '20': 'الألعاب', '24': 'الترفيه', '25': 'الأخبار' },
  bn: { ALL: 'সব বিভাগ', '10': 'সঙ্গীত', '17': 'খেলা', '20': 'গেমিং', '24': 'বিনোদন', '25': 'সংবাদ' },
  ur: { ALL: 'تمام زمرے', '10': 'موسیقی', '17': 'کھیل', '20': 'گیمنگ', '24': 'تفریح', '25': 'خبریں' },
  id: { ALL: 'Semua kategori', '10': 'Musik', '17': 'Olahraga', '20': 'Game', '24': 'Hiburan', '25': 'Berita' },
  ja: { ALL: 'すべてのカテゴリ', '10': '音楽', '17': 'スポーツ', '20': 'ゲーム', '24': 'エンタメ', '25': 'ニュース' },
  ru: { ALL: 'Все категории', '10': 'Музыка', '17': 'Спорт', '20': 'Игры', '24': 'Развлечения', '25': 'Новости' },
};

/** Localized region name (falls back to the English name passed in). */
export function regionName(lang: Lang, code: string, englishName: string): string {
  if (lang === 'en') return englishName;
  return REGION_NAMES[lang][code] ?? englishName;
}

/** Localized category name (falls back to the English name passed in). */
export function categoryName(lang: Lang, id: string, englishName: string): string {
  if (lang === 'en') return englishName;
  return CATEGORY_NAMES[lang][id] ?? englishName;
}

// --- SEO copy templates ------------------------------------------------------

export interface SeoCtx {
  hasCat: boolean;    // a specific category is selected
  isWorld: boolean;   // worldwide (region ALL)
  catName: string;    // already-localized category name (e.g. "Música")
  regionName: string; // already-localized region name (e.g. "México" / "todo el mundo")
}

export interface SeoStrings {
  title: string;
  description: string;
  h1: string;
  intro: string;
}

export interface FaqItem {
  q: string;
  a: string;
}

export interface UiPack {
  trendingTab: string;
  allTimeTab: string;
  suggest: (native: string) => string; // language-switch banner prompt
  dismiss: string;

  // Header (Layout.tsx)
  tagline: string;
  languageAriaLabel: string;
  themeToLight: string; // shown when currently dark, to switch to light
  themeToDark: string;  // shown when currently light, to switch to dark
  footerAbout: string;
  footerPoweredBy: string; // "Powered by" (before the YouTube Data API link)
  footerBuiltBy: string;   // "Built by" (before the Robles Consulting LLC link)

  // Filter bar (FilterBar.tsx)
  categoryLabel: string;
  regionLabel: string;
  shortsOn: string;
  shortsOff: string;
  shortsOnTitle: string;
  shortsOffTitle: string;
  randomLabel: string;
  randomTitle: string;
  shareLabel: string;
  shareCopiedLabel: string;
  shareTitle: string;

  // "How it works" + FAQ (InfoSection.tsx)
  infoTitle: string;
  infoBody: string;
  faqTitle: string;
  faq: FaqItem[];

  // Channel info panel (ChannelModal.tsx)
  channelLoading: string;
  channelSubscribers: string;
  channelVideosLabel: string;
  channelTotalViews: string;
  channelVisit: string;
  channelFullPage: string;
  channelOnChart: (n: number) => string;
  channelTrackedVideos: (n: number) => string;
}

export interface LangPack {
  chart(c: SeoCtx): SeoStrings;
  records(c: SeoCtx): SeoStrings;
  ui: UiPack;
}

const BRAND = ' — YouTubeMost';
// Region suffix for titles/H1s: worldwide uses the natural adverb; a specific
// region uses a "·" separator so we never fight per-language prepositions.
const sep = (c: SeoCtx, worldWord: string) => (c.isWorld ? ` ${worldWord}` : ` · ${c.regionName}`);

const PACKS: Record<Exclude<Lang, 'en'>, LangPack> = {
  es: {
    chart: (c) => {
      const cat = c.hasCat ? `de ${c.catName} ` : '';
      return {
        title: `Vídeos ${cat}más populares de YouTube${sep(c, 'en el mundo')}${BRAND}`,
        description: `Los vídeos ${cat}más vistos y populares de YouTube ${c.isWorld ? 'en todo el mundo' : 'en ' + c.regionName} ahora mismo, clasificados por número de visualizaciones y actualizados continuamente.`,
        h1: `Vídeos ${cat}más populares de YouTube${sep(c, 'en el mundo')}`,
        intro: `Los vídeos ${cat}más populares de YouTube en este momento ${c.isWorld ? 'en todo el mundo' : 'en ' + c.regionName}, clasificados por número de visualizaciones y actualizados continuamente. Toca cualquier vídeo para verlo aquí mismo.`,
      };
    },
    records: (c) => {
      const cat = c.hasCat ? `de ${c.catName} ` : '';
      return {
        title: `Los vídeos ${cat}más vistos de YouTube de todos los tiempos${BRAND}`,
        description: `Los vídeos ${cat}más vistos de YouTube de la historia, clasificados por número total de visualizaciones. Descubre qué vídeos tienen más visitas de todos los tiempos.`,
        h1: `Los vídeos ${cat}más vistos de YouTube de todos los tiempos`,
        intro: `Cada vídeo está clasificado por su número total de visualizaciones: los vídeos ${cat}con más visitas de la historia de YouTube. La clasificación se actualiza automáticamente cuando se baten récords.`,
      };
    },
    ui: {
      trendingTab: 'Tendencias', allTimeTab: 'Más vistos de siempre',
      suggest: (n) => `¿Ver YouTubeMost en ${n}?`, dismiss: 'No, gracias',
      tagline: 'Descubre los videos más vistos',
      languageAriaLabel: 'Idioma',
      themeToLight: 'Cambiar a modo claro',
      themeToDark: 'Cambiar a modo oscuro',
      footerAbout: 'YouTubeMost rastrea los videos más vistos y en tendencia de YouTube, actualizados continuamente. Explora los rankings más populares a nivel mundial o por país — Estados Unidos, Reino Unido, India, Japón, Brasil y más — y filtra por categoría como Música, Juegos, Deportes, Entretenimiento y Noticias. Es una forma rápida y sin distracciones de ver lo que el mundo está viendo en este momento.',
      footerPoweredBy: 'Desarrollado con',
      footerBuiltBy: 'Creado por',
      categoryLabel: 'Categoría',
      regionLabel: 'Región',
      shortsOn: 'Shorts: Activados',
      shortsOff: 'Shorts: Desactivados',
      shortsOnTitle: 'Los Shorts están incluidos — haz clic para ocultarlos',
      shortsOffTitle: 'Los Shorts están ocultos — haz clic para incluirlos',
      randomLabel: 'Aleatorio',
      randomTitle: 'Reproducir un video al azar (R)',
      shareLabel: 'Compartir',
      shareCopiedLabel: '¡Copiado!',
      shareTitle: 'Copiar un enlace a esta vista',
      infoTitle: 'Cómo funciona YouTubeMost',
      infoBody: 'YouTubeMost es una forma rápida y sin distracciones de ver los videos más vistos y en tendencia de YouTube en este momento. Leemos los rankings oficiales de "más populares" de la API de datos de YouTube para cada país y categoría, los ordenamos por número de vistas, y te dejamos reproducir cualquier video sin salir de la página. No hay recomendaciones, ni un pozo sin fondo de reproducción automática, ni un algoritmo cuestionando tus gustos — solo lo que el mundo realmente está viendo, actualizado continuamente.',
      faqTitle: 'Preguntas frecuentes',
      faq: [
        { q: '¿Cómo se ordenan los videos en tendencia?', a: 'YouTubeMost obtiene los videos más populares directamente de la API de datos oficial de YouTube (el mismo ranking de "más populares" que usa YouTube) para cada país y categoría, y luego los ordena por número de vistas. Siempre estás viendo datos reales de YouTube, no nuestra propia opinión.' },
        { q: '¿Con qué frecuencia se actualizan los datos?', a: 'Los rankings se actualizan continuamente — los datos se vuelven a obtener de YouTube y se almacenan en caché en el borde durante unos 10 minutos, así que lo que ves está muy cerca del tiempo real. Cada página muestra cuándo se actualizó por última vez.' },
        { q: '¿Puedo ver lo que está en tendencia en mi país?', a: 'Sí. Puedes explorar los videos en tendencia a nivel mundial o de un país en concreto — Estados Unidos, Reino Unido, India, Japón, Corea del Sur, Brasil, Alemania, Francia, Canadá, Australia, México y Rusia — y filtrar por categoría como Música, Juegos, Deportes, Entretenimiento y Noticias.' },
        { q: '¿Se incluyen los YouTube Shorts?', a: 'Por defecto los videos cortos están ocultos para que veas contenido de duración completa, pero puedes activar los Shorts en cualquier momento con el interruptor de Shorts.' },
        { q: '¿YouTubeMost está afiliado a YouTube?', a: 'No. YouTubeMost es una herramienta independiente que usa la API de datos pública de YouTube. Todos los videos, miniaturas y canales pertenecen a sus respectivos propietarios en YouTube.' },
      ],
      channelLoading: 'Cargando canal…',
      channelSubscribers: 'suscriptores',
      channelVideosLabel: 'videos',
      channelTotalViews: 'vistas totales',
      channelVisit: 'Visitar en YouTube',
      channelFullPage: 'Página completa del canal',
      channelOnChart: (n) => `En el ranking ahora mismo (${n})`,
      channelTrackedVideos: (n) => `Videos rastreados más vistos (${n})`,
    },
  },
  pt: {
    chart: (c) => {
      const cat = c.hasCat ? `de ${c.catName} ` : '';
      return {
        title: `Vídeos ${cat}mais populares do YouTube${sep(c, 'no mundo')}${BRAND}`,
        description: `Os vídeos ${cat}mais vistos e populares do YouTube ${c.isWorld ? 'no mundo todo' : 'em ' + c.regionName} agora, classificados por número de visualizações e atualizados continuamente.`,
        h1: `Vídeos ${cat}mais populares do YouTube${sep(c, 'no mundo')}`,
        intro: `Os vídeos ${cat}mais populares do YouTube neste momento ${c.isWorld ? 'no mundo todo' : 'em ' + c.regionName}, classificados por número de visualizações e atualizados continuamente. Toque em qualquer vídeo para assistir aqui mesmo.`,
      };
    },
    records: (c) => {
      const cat = c.hasCat ? `de ${c.catName} ` : '';
      return {
        title: `Os vídeos ${cat}mais vistos do YouTube de todos os tempos${BRAND}`,
        description: `Os vídeos ${cat}mais vistos do YouTube de todos os tempos, classificados pelo total de visualizações. Descubra quais vídeos têm mais visualizações da história.`,
        h1: `Os vídeos ${cat}mais vistos do YouTube de todos os tempos`,
        intro: `Cada vídeo é classificado pelo total de visualizações: os vídeos ${cat}mais assistidos da história do YouTube. A classificação é atualizada automaticamente sempre que um recorde é batido.`,
      };
    },
    ui: {
      trendingTab: 'Em alta', allTimeTab: 'Mais vistos de sempre',
      suggest: (n) => `Ver o YouTubeMost em ${n}?`, dismiss: 'Não, obrigado',
      tagline: 'Descubra os vídeos mais vistos',
      languageAriaLabel: 'Idioma',
      themeToLight: 'Mudar para o modo claro',
      themeToDark: 'Mudar para o modo escuro',
      footerAbout: 'O YouTubeMost acompanha os vídeos mais vistos e em alta no YouTube, atualizados continuamente. Explore os rankings mundiais ou por país — Estados Unidos, Reino Unido, Índia, Japão, Brasil e outros — e filtre por categoria, como Música, Games, Esportes, Entretenimento e Notícias. É um jeito rápido e sem distrações de ver o que o mundo está assistindo agora.',
      footerPoweredBy: 'Desenvolvido com',
      footerBuiltBy: 'Criado por',
      categoryLabel: 'Categoria',
      regionLabel: 'Região',
      shortsOn: 'Shorts: Ativado',
      shortsOff: 'Shorts: Desativado',
      shortsOnTitle: 'Os Shorts estão incluídos — clique para ocultá-los',
      shortsOffTitle: 'Os Shorts estão ocultos — clique para incluí-los',
      randomLabel: 'Aleatório',
      randomTitle: 'Tocar um vídeo aleatório (R)',
      shareLabel: 'Compartilhar',
      shareCopiedLabel: 'Copiado!',
      shareTitle: 'Copiar um link para esta visualização',
      infoTitle: 'Como o YouTubeMost funciona',
      infoBody: 'O YouTubeMost é um jeito rápido e sem distrações de ver os vídeos mais vistos e em alta no YouTube agora mesmo. Nós lemos os rankings oficiais de "mais populares" da API de Dados do YouTube para cada país e categoria, classificamos por número de visualizações e deixamos você assistir a qualquer vídeo sem sair da página. Não há recomendações, nem buracos de autoplay, nem um algoritmo questionando suas escolhas — apenas o que o mundo está realmente assistindo, atualizado continuamente.',
      faqTitle: 'Perguntas frequentes',
      faq: [
        { q: 'Como os vídeos em alta são classificados?', a: 'O YouTubeMost busca os vídeos mais populares diretamente da API de Dados do YouTube oficial (o mesmo ranking de "mais populares" que o YouTube usa) para cada país e categoria, e depois os classifica por número de visualizações. Você está sempre vendo dados reais do YouTube, não a nossa opinião.' },
        { q: 'Com que frequência os dados são atualizados?', a: 'Os rankings são atualizados continuamente — os dados são buscados novamente no YouTube e armazenados em cache na borda por cerca de 10 minutos, então o que você vê é bem próximo do tempo real. Cada página mostra quando foi atualizada por último.' },
        { q: 'Posso ver o que está em alta no meu país?', a: 'Sim. Você pode explorar os vídeos em alta no mundo todo ou em um país específico — Estados Unidos, Reino Unido, Índia, Japão, Coreia do Sul, Brasil, Alemanha, França, Canadá, Austrália, México e Rússia — e filtrar por categoria, como Música, Games, Esportes, Entretenimento e Notícias.' },
        { q: 'Os YouTube Shorts estão incluídos?', a: 'Por padrão, os vídeos curtos ficam ocultos para você ver o conteúdo completo, mas você pode ativar os Shorts a qualquer momento usando o botão de Shorts.' },
        { q: 'O YouTubeMost tem alguma relação com o YouTube?', a: 'Não. O YouTubeMost é uma ferramenta independente que usa a API de Dados do YouTube pública. Todos os vídeos, miniaturas e canais pertencem aos seus respectivos donos no YouTube.' },
      ],
      channelLoading: 'Carregando canal…',
      channelSubscribers: 'inscritos',
      channelVideosLabel: 'vídeos',
      channelTotalViews: 'visualizações totais',
      channelVisit: 'Visitar no YouTube',
      channelFullPage: 'Página completa do canal',
      channelOnChart: (n) => `No ranking agora (${n})`,
      channelTrackedVideos: (n) => `Vídeos mais vistos monitorados (${n})`,
    },
  },
  de: {
    chart: (c) => {
      const cat = c.hasCat ? `${c.catName}-` : '';
      return {
        title: `Beliebteste ${cat}YouTube-Videos${sep(c, 'weltweit')}${BRAND}`,
        description: `Die meistgesehenen und beliebtesten ${cat}YouTube-Videos ${c.isWorld ? 'weltweit' : 'in ' + c.regionName} gerade jetzt, sortiert nach Aufrufen und laufend aktualisiert.`,
        h1: `Beliebteste ${cat}YouTube-Videos${sep(c, 'weltweit')}`,
        intro: `Die beliebtesten ${cat}YouTube-Videos im Moment ${c.isWorld ? 'weltweit' : 'in ' + c.regionName}, sortiert nach Aufrufzahl und laufend aktualisiert. Tippe auf ein Video, um es direkt hier anzusehen.`,
      };
    },
    records: (c) => {
      const cat = c.hasCat ? `${c.catName}-` : '';
      return {
        title: `Meistgesehene ${cat}YouTube-Videos aller Zeiten${BRAND}`,
        description: `Die meistgesehenen ${cat}YouTube-Videos aller Zeiten, sortiert nach der Gesamtzahl der Aufrufe. Sieh dir an, welche Videos die meisten Aufrufe überhaupt haben.`,
        h1: `Meistgesehene ${cat}YouTube-Videos aller Zeiten`,
        intro: `Jedes Video ist nach seiner Gesamtaufrufzahl sortiert – die ${cat}YouTube-Videos mit den meisten Aufrufen aller Zeiten. Die Rangliste aktualisiert sich automatisch, sobald ein Rekord fällt.`,
      };
    },
    ui: {
      trendingTab: 'Im Trend', allTimeTab: 'Meistgesehen aller Zeiten',
      suggest: (n) => `YouTubeMost auf ${n} ansehen?`, dismiss: 'Nein, danke',
      tagline: 'Entdecke die meistgesehenen Videos',
      languageAriaLabel: 'Sprache',
      themeToLight: 'Zum hellen Modus wechseln',
      themeToDark: 'Zum dunklen Modus wechseln',
      footerAbout: 'YouTubeMost verfolgt die meistgesehenen und angesagten Videos auf YouTube, laufend aktualisiert. Durchsuche die Top-Charts weltweit oder nach Land — USA, Vereinigtes Königreich, Indien, Japan, Brasilien und mehr — und filtere nach Kategorie wie Musik, Gaming, Sport, Unterhaltung und Nachrichten. So siehst du schnell und ohne Ablenkung, was die Welt gerade schaut.',
      footerPoweredBy: 'Bereitgestellt von',
      footerBuiltBy: 'Entwickelt von',
      categoryLabel: 'Kategorie',
      regionLabel: 'Region',
      shortsOn: 'Shorts: An',
      shortsOff: 'Shorts: Aus',
      shortsOnTitle: 'Shorts werden angezeigt — klicke, um sie auszublenden',
      shortsOffTitle: 'Shorts sind ausgeblendet — klicke, um sie einzublenden',
      randomLabel: 'Zufall',
      randomTitle: 'Ein zufälliges Video abspielen (R)',
      shareLabel: 'Teilen',
      shareCopiedLabel: 'Kopiert!',
      shareTitle: 'Link zu dieser Ansicht kopieren',
      infoTitle: 'So funktioniert YouTubeMost',
      infoBody: 'YouTubeMost zeigt dir schnell und ohne Ablenkung, welche Videos auf YouTube gerade am meisten gesehen werden und im Trend liegen. Wir lesen die offiziellen "Meistpopulär"-Charts der YouTube Data API für jedes Land und jede Kategorie aus, sortieren sie nach Aufrufen und lassen dich jedes Video abspielen, ohne die Seite zu verlassen. Es gibt keine Empfehlungen, keine Autoplay-Kaninchenbauten und keinen Algorithmus, der dich zu etwas anderem überredet — nur das, was die Welt tatsächlich schaut, laufend aktualisiert.',
      faqTitle: 'Häufig gestellte Fragen',
      faq: [
        { q: 'Wie werden die angesagten Videos eingestuft?', a: 'YouTubeMost bezieht die beliebtesten Videos direkt aus der offiziellen YouTube Data API (demselben "Meistpopulär"-Chart, den YouTube selbst verwendet) für jedes Land und jede Kategorie und sortiert sie nach Aufrufen. Du siehst also immer echte YouTube-Daten, nicht unsere eigene Meinung.' },
        { q: 'Wie oft werden die Daten aktualisiert?', a: 'Die Charts werden laufend aktualisiert — die Daten werden erneut von YouTube abgerufen und für etwa 10 Minuten am Edge zwischengespeichert, sodass du nahezu Echtzeit-Ergebnisse siehst. Jede Seite zeigt an, wann sie zuletzt aktualisiert wurde.' },
        { q: 'Kann ich sehen, was in meinem Land im Trend liegt?', a: 'Ja. Du kannst angesagte Videos weltweit oder für ein bestimmtes Land durchsuchen — USA, Vereinigtes Königreich, Indien, Japan, Südkorea, Brasilien, Deutschland, Frankreich, Kanada, Australien, Mexiko und Russland — und nach Kategorie wie Musik, Gaming, Sport, Unterhaltung und Nachrichten filtern.' },
        { q: 'Sind YouTube Shorts enthalten?', a: 'Standardmäßig sind kurze Videos ausgeblendet, damit du Inhalte in voller Länge siehst, aber du kannst Shorts jederzeit über den Shorts-Schalter einblenden.' },
        { q: 'Ist YouTubeMost mit YouTube verbunden?', a: 'Nein. YouTubeMost ist ein unabhängiges Tool, das die öffentliche YouTube Data API nutzt. Alle Videos, Thumbnails und Kanäle gehören ihren jeweiligen Eigentümern auf YouTube.' },
      ],
      channelLoading: 'Kanal wird geladen…',
      channelSubscribers: 'Abonnenten',
      channelVideosLabel: 'Videos',
      channelTotalViews: 'Aufrufe insgesamt',
      channelVisit: 'Auf YouTube ansehen',
      channelFullPage: 'Vollständige Kanalseite',
      channelOnChart: (n) => `Aktuell in den Charts (${n})`,
      channelTrackedVideos: (n) => `Meistgesehene erfasste Videos (${n})`,
    },
  },
  fr: {
    chart: (c) => {
      const cat = c.hasCat ? `de ${c.catName} ` : '';
      return {
        title: `Vidéos ${cat}les plus populaires sur YouTube${sep(c, 'dans le monde')}${BRAND}`,
        description: `Les vidéos ${cat}les plus vues et les plus populaires sur YouTube ${c.isWorld ? 'dans le monde' : 'en ' + c.regionName} en ce moment, classées par nombre de vues et actualisées en continu.`,
        h1: `Vidéos ${cat}les plus populaires sur YouTube${sep(c, 'dans le monde')}`,
        intro: `Les vidéos ${cat}les plus populaires sur YouTube en ce moment ${c.isWorld ? 'dans le monde' : 'en ' + c.regionName}, classées par nombre de vues et actualisées en continu. Touchez une vidéo pour la regarder ici même.`,
      };
    },
    records: (c) => {
      const cat = c.hasCat ? `de ${c.catName} ` : '';
      return {
        title: `Vidéos ${cat}les plus vues sur YouTube de tous les temps${BRAND}`,
        description: `Les vidéos ${cat}les plus vues sur YouTube de tous les temps, classées par nombre total de vues. Découvrez quelles vidéos comptent le plus de vues de l'histoire.`,
        h1: `Vidéos ${cat}les plus vues sur YouTube de tous les temps`,
        intro: `Chaque vidéo est classée selon son nombre total de vues : les vidéos ${cat}les plus regardées de l'histoire de YouTube. Le classement se met à jour automatiquement à chaque nouveau record.`,
      };
    },
    ui: {
      trendingTab: 'Tendances', allTimeTab: 'Les plus vues',
      suggest: (n) => `Voir YouTubeMost en ${n} ?`, dismiss: 'Non merci',
      tagline: 'Découvrez les vidéos les plus vues',
      languageAriaLabel: 'Langue',
      themeToLight: 'Passer au mode clair',
      themeToDark: 'Passer au mode sombre',
      footerAbout: "YouTubeMost suit les vidéos les plus vues et les plus tendances sur YouTube, mises à jour en continu. Parcourez les classements les plus populaires dans le monde ou par pays — États-Unis, Royaume-Uni, Inde, Japon, Brésil et plus encore — et filtrez par catégorie comme Musique, Jeux vidéo, Sport, Divertissement et Actualités. C'est une façon rapide et sans distraction de voir ce que le monde regarde en ce moment.",
      footerPoweredBy: 'Propulsé par',
      footerBuiltBy: 'Créé par',
      categoryLabel: 'Catégorie',
      regionLabel: 'Région',
      shortsOn: 'Shorts : activés',
      shortsOff: 'Shorts : désactivés',
      shortsOnTitle: 'Les Shorts sont inclus — cliquez pour les masquer',
      shortsOffTitle: 'Les Shorts sont masqués — cliquez pour les inclure',
      randomLabel: 'Aléatoire',
      randomTitle: 'Lire une vidéo aléatoire (R)',
      shareLabel: 'Partager',
      shareCopiedLabel: 'Copié !',
      shareTitle: 'Copier un lien vers cette vue',
      infoTitle: 'Comment fonctionne YouTubeMost',
      infoBody: "YouTubeMost est une façon rapide et sans distraction de voir les vidéos les plus vues et les plus tendances sur YouTube en ce moment. Nous récupérons les classements officiels des vidéos \"les plus populaires\" depuis l'API YouTube Data pour chaque pays et catégorie, nous les classons par nombre de vues, et vous pouvez lire n'importe quelle vidéo sans quitter la page. Il n'y a ni recommandations, ni engrenage de lecture automatique, ni algorithme qui devine à votre place — seulement ce que le monde regarde réellement, mis à jour en continu.",
      faqTitle: 'Questions fréquentes',
      faq: [
        { q: 'Comment les vidéos tendances sont-elles classées ?', a: "YouTubeMost récupère les vidéos les plus populaires directement depuis l'API officielle YouTube Data (le même classement \"les plus populaires\" utilisé par YouTube) pour chaque pays et catégorie, puis les classe par nombre de vues. Vous voyez toujours des données YouTube réelles, pas notre propre avis." },
        { q: 'À quelle fréquence les données sont-elles mises à jour ?', a: 'Les classements sont actualisés en continu — les données sont récupérées depuis YouTube et mises en cache en périphérie pendant environ 10 minutes, donc ce que vous voyez est proche du temps réel. Chaque page indique la date de sa dernière mise à jour.' },
        { q: 'Puis-je voir ce qui est tendance dans mon pays ?', a: "Oui. Vous pouvez parcourir les vidéos tendances dans le monde entier ou pour un pays spécifique — les États-Unis, le Royaume-Uni, l'Inde, le Japon, la Corée du Sud, le Brésil, l'Allemagne, la France, le Canada, l'Australie, le Mexique et la Russie — et filtrer par catégorie comme Musique, Jeux vidéo, Sport, Divertissement et Actualités." },
        { q: 'Les YouTube Shorts sont-ils inclus ?', a: 'Par défaut, les vidéos courtes sont masquées afin que vous voyiez du contenu long format, mais vous pouvez activer les Shorts à tout moment grâce à l\'interrupteur Shorts.' },
        { q: 'YouTubeMost est-il affilié à YouTube ?', a: "Non. YouTubeMost est un outil indépendant qui utilise l'API publique YouTube Data. Toutes les vidéos, miniatures et chaînes appartiennent à leurs propriétaires respectifs sur YouTube." },
      ],
      channelLoading: 'Chargement de la chaîne…',
      channelSubscribers: 'abonnés',
      channelVideosLabel: 'vidéos',
      channelTotalViews: 'vues totales',
      channelVisit: 'Voir sur YouTube',
      channelFullPage: 'Page complète de la chaîne',
      channelOnChart: (n) => `Actuellement au classement (${n})`,
      channelTrackedVideos: (n) => `Vidéos suivies les plus vues (${n})`,
    },
  },

  // Larger-population languages. Titles/H1s use a "· region · category" tag
  // format to stay grammatical without inflection/preposition agreement;
  // prose (description/intro) is natural per language. Native review welcome.
  hi: {
    chart: (c) => {
      const reg = c.isWorld ? 'दुनिया भर' : c.regionName;
      const tag = c.hasCat ? ` · ${c.catName}` : '';
      const cat = c.hasCat ? `${c.catName} ` : '';
      return {
        title: `सबसे लोकप्रिय YouTube वीडियो · ${reg}${tag}${BRAND}`,
        description: `${c.isWorld ? 'दुनिया भर में' : c.regionName + ' में'} अभी YouTube पर सबसे लोकप्रिय ${cat}वीडियो, व्यूज़ के आधार पर क्रमबद्ध और लगातार अपडेट।`,
        h1: `सबसे लोकप्रिय YouTube वीडियो · ${reg}${tag}`,
        intro: `${c.isWorld ? 'दुनिया भर में' : c.regionName + ' में'} अभी सबसे लोकप्रिय ${cat}वीडियो। किसी भी वीडियो पर टैप करके यहीं देखें।`,
      };
    },
    records: (c) => {
      const tag = c.hasCat ? ` · ${c.catName}` : '';
      return {
        title: `अब तक के सबसे ज़्यादा देखे गए YouTube वीडियो${tag}${BRAND}`,
        description: `अब तक के सबसे ज़्यादा देखे गए YouTube वीडियो, कुल व्यूज़ के आधार पर क्रमबद्ध।`,
        h1: `अब तक के सबसे ज़्यादा देखे गए YouTube वीडियो${tag}`,
        intro: `हर वीडियो कुल व्यूज़ के आधार पर क्रमबद्ध — YouTube के इतिहास के सबसे ज़्यादा देखे गए वीडियो।`,
      };
    },
    ui: {
      trendingTab: 'ट्रेंडिंग', allTimeTab: 'सर्वकालिक टॉप',
      suggest: (n) => `YouTubeMost को ${n} में देखें?`, dismiss: 'नहीं',
      tagline: 'सबसे ज़्यादा देखे गए वीडियो खोजें',
      languageAriaLabel: 'भाषा',
      themeToLight: 'लाइट मोड पर जाएं',
      themeToDark: 'डार्क मोड पर जाएं',
      footerAbout: 'YouTubeMost, YouTube पर सबसे ज़्यादा देखे गए और ट्रेंडिंग वीडियो को लगातार अपडेट करते हुए ट्रैक करता है। दुनिया भर के या किसी देश के टॉप चार्ट ब्राउज़ करें — संयुक्त राज्य, यूनाइटेड किंगडम, भारत, जापान, ब्राज़ील और भी बहुत कुछ — और म्यूज़िक, गेमिंग, स्पोर्ट्स, एंटरटेनमेंट और न्यूज़ जैसी कैटेगरी से फ़िल्टर करें। दुनिया अभी क्या देख रही है, यह जानने का यह एक तेज़ और झंझट-मुक्त तरीका है।',
      footerPoweredBy: 'पावर्ड बाय',
      footerBuiltBy: 'बनाया',
      categoryLabel: 'कैटेगरी',
      regionLabel: 'क्षेत्र',
      shortsOn: 'शॉर्ट्स: ऑन',
      shortsOff: 'शॉर्ट्स: ऑफ़',
      shortsOnTitle: 'शॉर्ट्स शामिल हैं — इन्हें छिपाने के लिए क्लिक करें',
      shortsOffTitle: 'शॉर्ट्स छिपे हैं — इन्हें शामिल करने के लिए क्लिक करें',
      randomLabel: 'रैंडम',
      randomTitle: 'कोई रैंडम वीडियो चलाएं (R)',
      shareLabel: 'शेयर',
      shareCopiedLabel: 'कॉपी हो गया!',
      shareTitle: 'इस व्यू का लिंक कॉपी करें',
      infoTitle: 'YouTubeMost कैसे काम करता है',
      infoBody: 'YouTubeMost, YouTube पर अभी सबसे ज़्यादा देखे गए और ट्रेंडिंग वीडियो देखने का एक तेज़ और झंझट-मुक्त तरीका है। हम हर देश और कैटेगरी के लिए YouTube Data API से आधिकारिक "मोस्ट पॉपुलर" चार्ट पढ़ते हैं, उन्हें व्यूज़ की संख्या के आधार पर रैंक करते हैं, और आपको पेज छोड़े बिना कोई भी वीडियो चलाने देते हैं। यहां न कोई रिकमेंडेशन है, न ऑटोप्ले का चक्कर, न कोई एल्गोरिदम आपके लिए फ़ैसले ले रहा है — बस वही जो दुनिया असल में देख रही है, लगातार अपडेट होता हुआ।',
      faqTitle: 'अक्सर पूछे जाने वाले सवाल',
      faq: [
        { q: 'ट्रेंडिंग वीडियो कैसे रैंक किए जाते हैं?', a: 'YouTubeMost हर देश और कैटेगरी के लिए सीधे आधिकारिक YouTube Data API (वही "मोस्ट पॉपुलर" चार्ट जो YouTube खुद इस्तेमाल करता है) से सबसे पॉपुलर वीडियो लेता है, फिर उन्हें व्यूज़ की संख्या के आधार पर रैंक करता है। आप हमेशा असली YouTube डेटा देख रहे हैं, हमारी अपनी राय नहीं।' },
        { q: 'डेटा कितनी बार अपडेट होता है?', a: 'चार्ट लगातार रिफ्रेश होते हैं — डेटा YouTube से फिर से फ़ेच किया जाता है और एज पर लगभग 10 मिनट तक कैश होता है, इसलिए आप जो देखते हैं वह रीयल टाइम के काफ़ी करीब है। हर पेज पर बताया जाता है कि इसे आख़िरी बार कब अपडेट किया गया था।' },
        { q: 'क्या मैं अपने देश में क्या ट्रेंड कर रहा है देख सकता हूं?', a: 'हां। आप दुनिया भर के या किसी खास देश के ट्रेंडिंग वीडियो ब्राउज़ कर सकते हैं — संयुक्त राज्य, यूनाइटेड किंगडम, भारत, जापान, दक्षिण कोरिया, ब्राज़ील, जर्मनी, फ़्रांस, कनाडा, ऑस्ट्रेलिया, मैक्सिको और रूस — और म्यूज़िक, गेमिंग, स्पोर्ट्स, एंटरटेनमेंट और न्यूज़ जैसी कैटेगरी से फ़िल्टर कर सकते हैं।' },
        { q: 'क्या YouTube Shorts भी शामिल हैं?', a: 'डिफ़ॉल्ट रूप से शॉर्ट वीडियो छिपे रहते हैं ताकि आपको फ़ुल-लेंथ कॉन्टेंट दिखे, लेकिन आप कभी भी Shorts स्विच से Shorts को ऑन कर सकते हैं।' },
        { q: 'क्या YouTubeMost, YouTube से जुड़ा हुआ है?', a: 'नहीं। YouTubeMost एक स्वतंत्र टूल है जो पब्लिक YouTube Data API का इस्तेमाल करता है। सभी वीडियो, थंबनेल और चैनल YouTube पर उनके संबंधित मालिकों के हैं।' },
      ],
      channelLoading: 'चैनल लोड हो रहा है…',
      channelSubscribers: 'सब्सक्राइबर',
      channelVideosLabel: 'वीडियो',
      channelTotalViews: 'कुल व्यूज़',
      channelVisit: 'YouTube पर देखें',
      channelFullPage: 'पूरा चैनल पेज',
      channelOnChart: (n) => `अभी चार्ट पर (${n})`,
      channelTrackedVideos: (n) => `सबसे ज़्यादा देखे गए ट्रैक किए गए वीडियो (${n})`,
    },
  },
  zh: {
    chart: (c) => {
      const reg = c.isWorld ? '全球' : c.regionName;
      const tag = c.hasCat ? ` · ${c.catName}` : '';
      const cat = c.hasCat ? c.catName : '';
      return {
        title: `最热门的 YouTube 视频 · ${reg}${tag}${BRAND}`,
        description: `${reg}当前观看量最高的${cat}YouTube 视频，按观看次数排名并持续更新。`,
        h1: `最热门的 YouTube 视频 · ${reg}${tag}`,
        intro: `${reg}当前最热门的${cat}视频，按观看次数排名。点按任意视频即可在此观看。`,
      };
    },
    records: (c) => {
      const tag = c.hasCat ? ` · ${c.catName}` : '';
      const cat = c.hasCat ? c.catName : '';
      return {
        title: `史上观看量最高的 YouTube 视频${tag}${BRAND}`,
        description: `史上观看量最高的${cat}YouTube 视频，按总观看次数排名。`,
        h1: `史上观看量最高的 YouTube 视频${tag}`,
        intro: `每个视频都按总观看次数排名——YouTube 史上观看量最高的视频。`,
      };
    },
    ui: {
      trendingTab: '正在流行', allTimeTab: '史上最热',
      suggest: (n) => `用${n}查看 YouTubeMost？`, dismiss: '不用了',
      tagline: '发现最热门的观看视频',
      languageAriaLabel: '语言',
      themeToLight: '切换到浅色模式',
      themeToDark: '切换到深色模式',
      footerAbout: 'YouTubeMost 持续追踪 YouTube 上观看次数最多、最受欢迎的视频。您可以浏览全球排行榜，也可以按国家查看——美国、英国、印度、日本、巴西等——并按音乐、游戏、体育、娱乐、新闻等分类筛选。这是一种快速、无干扰的方式，让您即时了解全世界正在看什么。',
      footerPoweredBy: '技术支持',
      footerBuiltBy: '开发者',
      categoryLabel: '分类',
      regionLabel: '地区',
      shortsOn: 'Shorts：开启',
      shortsOff: 'Shorts：关闭',
      shortsOnTitle: '已显示 Shorts——点击可隐藏',
      shortsOffTitle: '已隐藏 Shorts——点击可显示',
      randomLabel: '随机',
      randomTitle: '播放随机视频（R）',
      shareLabel: '分享',
      shareCopiedLabel: '已复制！',
      shareTitle: '复制此页面的链接',
      infoTitle: 'YouTubeMost 如何运作',
      infoBody: 'YouTubeMost 是一种快速、无干扰的方式，让您即时查看 YouTube 上观看次数最多、最受欢迎的视频。我们通过 YouTube Data API 读取每个国家和分类的官方"最受欢迎"榜单，按观看次数排序，并让您无需离开页面即可播放任意视频。这里没有推荐算法，没有自动播放的无底洞，也没有算法替您做决定——只呈现全世界真正在看的内容，并持续更新。',
      faqTitle: '常见问题',
      faq: [
        { q: '热门视频是如何排名的？', a: 'YouTubeMost 直接从官方 YouTube Data API（与 YouTube 使用的"最受欢迎"榜单相同）获取每个国家和分类下最受欢迎的视频，然后按观看次数排名。您看到的始终是真实的 YouTube 数据，而非我们的主观判断。' },
        { q: '数据多久更新一次？', a: '榜单会持续刷新——数据从 YouTube 重新获取后，会在边缘节点缓存约 10 分钟，因此您看到的内容近乎实时。每个页面都会显示上次更新的时间。' },
        { q: '我可以查看我所在国家的热门视频吗？', a: '可以。您可以浏览全球热门视频，也可以查看特定国家的榜单——美国、英国、印度、日本、韩国、巴西、德国、法国、加拿大、澳大利亚、墨西哥和俄罗斯——并按音乐、游戏、体育、娱乐、新闻等分类筛选。' },
        { q: 'YouTube Shorts 是否包含在内？', a: '默认情况下，短视频会被隐藏，以便您查看完整长度的内容，但您可以随时使用 Shorts 开关将其开启。' },
        { q: 'YouTubeMost 是否与 YouTube 有关联？', a: '没有。YouTubeMost 是一个使用公开 YouTube Data API 的独立工具。所有视频、缩略图和频道均归其在 YouTube 上的相应所有者所有。' },
      ],
      channelLoading: '正在加载频道…',
      channelSubscribers: '订阅者',
      channelVideosLabel: '视频',
      channelTotalViews: '总观看次数',
      channelVisit: '在 YouTube 上查看',
      channelFullPage: '查看完整频道页面',
      channelOnChart: (n) => `当前上榜（${n}）`,
      channelTrackedVideos: (n) => `观看次数最多的追踪视频（${n}）`,
    },
  },
  ar: {
    chart: (c) => {
      const reg = c.isWorld ? 'العالم' : c.regionName;
      const tag = c.hasCat ? ` · ${c.catName}` : '';
      return {
        title: `أكثر مقاطع فيديو يوتيوب مشاهدةً · ${reg}${tag}${BRAND}`,
        description: `أكثر مقاطع الفيديو مشاهدةً ورواجًا على يوتيوب ${c.isWorld ? 'في العالم' : 'في ' + c.regionName} الآن، مرتبة حسب المشاهدات وتُحدَّث باستمرار.`,
        h1: `أكثر مقاطع فيديو يوتيوب مشاهدةً · ${reg}${tag}`,
        intro: `أكثر مقاطع الفيديو مشاهدةً على يوتيوب ${c.isWorld ? 'في العالم' : 'في ' + c.regionName} الآن. انقر على أي فيديو لمشاهدته هنا.`,
      };
    },
    records: (c) => {
      const tag = c.hasCat ? ` · ${c.catName}` : '';
      return {
        title: `أكثر مقاطع فيديو يوتيوب مشاهدةً على الإطلاق${tag}${BRAND}`,
        description: `أكثر مقاطع فيديو يوتيوب مشاهدةً على الإطلاق، مرتبة حسب إجمالي المشاهدات.`,
        h1: `أكثر مقاطع فيديو يوتيوب مشاهدةً على الإطلاق${tag}`,
        intro: `كل فيديو مرتب حسب إجمالي مشاهداته — الأكثر مشاهدةً في تاريخ يوتيوب.`,
      };
    },
    ui: {
      trendingTab: 'الرائج', allTimeTab: 'الأكثر مشاهدة',
      suggest: (n) => `عرض YouTubeMost بـ${n}؟`, dismiss: 'لا شكرًا',
      tagline: 'اكتشف الفيديوهات الأكثر مشاهدة',
      languageAriaLabel: 'اللغة',
      themeToLight: 'التبديل إلى الوضع الفاتح',
      themeToDark: 'التبديل إلى الوضع الداكن',
      footerAbout: 'يتابع YouTubeMost الفيديوهات الأكثر مشاهدة والرائجة على YouTube، ويتم تحديثها باستمرار. تصفّح القوائم الأعلى على مستوى العالم أو بحسب الدولة - الولايات المتحدة، المملكة المتحدة، الهند، اليابان، البرازيل وغيرها - وصنّفها بحسب الفئة مثل الموسيقى، الألعاب، الرياضة، الترفيه والأخبار. إنها طريقة سريعة وخالية من التشويش لمعرفة ما يشاهده العالم الآن.',
      footerPoweredBy: 'بدعم من',
      footerBuiltBy: 'من إنشاء',
      categoryLabel: 'الفئة',
      regionLabel: 'المنطقة',
      shortsOn: 'الفيديوهات القصيرة: مفعّلة',
      shortsOff: 'الفيديوهات القصيرة: معطّلة',
      shortsOnTitle: 'الفيديوهات القصيرة مضمّنة - اضغط لإخفائها',
      shortsOffTitle: 'الفيديوهات القصيرة مخفية - اضغط لتضمينها',
      randomLabel: 'عشوائي',
      randomTitle: 'تشغيل فيديو عشوائي (R)',
      shareLabel: 'مشاركة',
      shareCopiedLabel: 'تم النسخ!',
      shareTitle: 'نسخ رابط لهذا العرض',
      infoTitle: 'كيف يعمل YouTubeMost',
      infoBody: 'YouTubeMost هو طريقة سريعة وخالية من التشويش لمعرفة الفيديوهات الأكثر مشاهدة والرائجة على YouTube الآن. نقرأ قوائم "الأكثر شهرة" الرسمية من YouTube Data API لكل دولة وفئة، ونرتّبها بحسب عدد المشاهدات، ونتيح لك تشغيل أي فيديو دون مغادرة الصفحة. لا توجد اقتراحات، ولا حلقات تشغيل تلقائي لا تنتهي، ولا خوارزمية تفرض رأيها عليك - فقط ما يشاهده العالم فعليًا، بتحديث مستمر.',
      faqTitle: 'الأسئلة الشائعة',
      faq: [
        { q: 'كيف يتم ترتيب الفيديوهات الرائجة؟', a: 'يسحب YouTubeMost الفيديوهات الأكثر شهرة مباشرة من YouTube Data API الرسمي (نفس قائمة "الأكثر شهرة" التي يستخدمها YouTube) لكل دولة وفئة، ثم يرتّبها بحسب عدد المشاهدات. أنت تشاهد دائمًا بيانات YouTube الحقيقية، لا رأينا الخاص.' },
        { q: 'كم مرة يتم تحديث البيانات؟', a: 'تُحدَّث القوائم باستمرار - تُجلب البيانات من جديد من YouTube وتُخزَّن مؤقتًا عند الحافة لمدة تقارب 10 دقائق، لذا ما تراه قريب من الوقت الفعلي. تُظهر كل صفحة وقت آخر تحديث لها.' },
        { q: 'هل يمكنني معرفة ما هو رائج في بلدي؟', a: 'نعم. يمكنك تصفّح الفيديوهات الرائجة على مستوى العالم أو لدولة معيّنة - الولايات المتحدة، المملكة المتحدة، الهند، اليابان، كوريا الجنوبية، البرازيل، ألمانيا، فرنسا، كندا، أستراليا، المكسيك وروسيا - وتصنيفها بحسب فئة مثل الموسيقى، الألعاب، الرياضة، الترفيه والأخبار.' },
        { q: 'هل تُدرَج فيديوهات YouTube Shorts؟', a: 'بشكل افتراضي، تكون الفيديوهات القصيرة مخفية لتتمكن من رؤية المحتوى الكامل، ولكن يمكنك تفعيل الفيديوهات القصيرة في أي وقت باستخدام مفتاح Shorts.' },
        { q: 'هل YouTubeMost تابع لـ YouTube؟', a: 'لا. YouTubeMost أداة مستقلة تستخدم YouTube Data API العام. جميع الفيديوهات والصور المصغّرة والقنوات مملوكة لأصحابها على YouTube.' },
      ],
      channelLoading: 'جارٍ تحميل القناة…',
      channelSubscribers: 'مشترك',
      channelVideosLabel: 'فيديو',
      channelTotalViews: 'إجمالي المشاهدات',
      channelVisit: 'زيارة على YouTube',
      channelFullPage: 'صفحة القناة الكاملة',
      channelOnChart: (n) => `في القائمة الآن (${n})`,
      channelTrackedVideos: (n) => `الفيديوهات الأكثر مشاهدة المتابَعة (${n})`,
    },
  },
  bn: {
    chart: (c) => {
      const reg = c.isWorld ? 'বিশ্বজুড়ে' : c.regionName;
      const tag = c.hasCat ? ` · ${c.catName}` : '';
      const cat = c.hasCat ? `${c.catName} ` : '';
      return {
        title: `সবচেয়ে জনপ্রিয় YouTube ভিডিও · ${reg}${tag}${BRAND}`,
        description: `${c.isWorld ? 'বিশ্বজুড়ে' : c.regionName + '-এ'} এখন YouTube-এর সবচেয়ে বেশি দেখা ${cat}ভিডিও, ভিউ অনুযায়ী সাজানো ও নিয়মিত আপডেট।`,
        h1: `সবচেয়ে জনপ্রিয় YouTube ভিডিও · ${reg}${tag}`,
        intro: `${c.isWorld ? 'বিশ্বজুড়ে' : c.regionName + '-এ'} এখন সবচেয়ে জনপ্রিয় ${cat}ভিডিও। যেকোনো ভিডিওতে ট্যাপ করে এখানেই দেখুন।`,
      };
    },
    records: (c) => {
      const tag = c.hasCat ? ` · ${c.catName}` : '';
      return {
        title: `সর্বকালের সবচেয়ে বেশি দেখা YouTube ভিডিও${tag}${BRAND}`,
        description: `সর্বকালের সবচেয়ে বেশি দেখা YouTube ভিডিও, মোট ভিউ অনুযায়ী সাজানো।`,
        h1: `সর্বকালের সবচেয়ে বেশি দেখা YouTube ভিডিও${tag}`,
        intro: `প্রতিটি ভিডিও মোট ভিউ অনুযায়ী সাজানো — YouTube-এর ইতিহাসের সবচেয়ে বেশি দেখা ভিডিও।`,
      };
    },
    ui: {
      trendingTab: 'ট্রেন্ডিং', allTimeTab: 'সর্বকালের সেরা',
      suggest: (n) => `YouTubeMost ${n}-এ দেখবেন?`, dismiss: 'না',
      tagline: 'সবচেয়ে বেশি দেখা ভিডিও খুঁজুন',
      languageAriaLabel: 'ভাষা',
      themeToLight: 'লাইট মোডে যান',
      themeToDark: 'ডার্ক মোডে যান',
      footerAbout: 'YouTubeMost ইউটিউবের সবচেয়ে বেশি দেখা এবং ট্রেন্ডিং ভিডিওগুলো নিয়মিত ট্র্যাক করে। বিশ্বব্যাপী বা দেশ অনুযায়ী — যুক্তরাষ্ট্র, যুক্তরাজ্য, ভারত, জাপান, ব্রাজিল এবং আরও অনেক দেশের — টপ চার্ট ব্রাউজ করুন এবং মিউজিক, গেমিং, স্পোর্টস, বিনোদন ও সংবাদের মতো ক্যাটাগরি দিয়ে ফিল্টার করুন। এখন বিশ্ব কী দেখছে, তা জানার এটি একটি ঝটপট আর ঝামেলাহীন উপায়।',
      footerPoweredBy: 'চালিত',
      footerBuiltBy: 'নির্মাতা',
      categoryLabel: 'ক্যাটাগরি',
      regionLabel: 'অঞ্চল',
      shortsOn: 'শর্টস: চালু',
      shortsOff: 'শর্টস: বন্ধ',
      shortsOnTitle: 'শর্টস দেখানো হচ্ছে — লুকাতে ক্লিক করুন',
      shortsOffTitle: 'শর্টস লুকানো আছে — দেখাতে ক্লিক করুন',
      randomLabel: 'র‍্যান্ডম',
      randomTitle: 'একটি র‍্যান্ডম ভিডিও চালান (R)',
      shareLabel: 'শেয়ার',
      shareCopiedLabel: 'কপি হয়েছে!',
      shareTitle: 'এই ভিউয়ের লিংক কপি করুন',
      infoTitle: 'YouTubeMost কীভাবে কাজ করে',
      infoBody: 'YouTubeMost হলো এখন ইউটিউবে সবচেয়ে বেশি দেখা ও ট্রেন্ডিং ভিডিও দেখার একটি ঝটপট, ঝামেলাহীন উপায়। আমরা প্রতিটি দেশ ও ক্যাটাগরির জন্য YouTube Data API থেকে অফিসিয়াল "most popular" চার্ট পড়ি, ভিউ সংখ্যা অনুযায়ী র‍্যাংক করি, এবং পেজ ছাড়াই ভিডিও চালাতে দিই। এখানে কোনো রিকমেন্ডেশন নেই, অটোপ্লে-র চক্করে পড়া নেই, আর অ্যালগরিদমের অনুমান-নির্ভরতা নেই — বিশ্ব আসলে কী দেখছে, সেটাই দেখানো হয়, নিয়মিত আপডেট হতে থাকে।',
      faqTitle: 'সাধারণ জিজ্ঞাসা',
      faq: [
        { q: 'ট্রেন্ডিং ভিডিওগুলো কীভাবে র‍্যাংক করা হয়?', a: 'YouTubeMost প্রতিটি দেশ ও ক্যাটাগরির জন্য সরাসরি অফিসিয়াল YouTube Data API থেকে (ইউটিউব যে "most popular" চার্ট ব্যবহার করে, সেটাই) সবচেয়ে জনপ্রিয় ভিডিওগুলো নিয়ে আসে, তারপর ভিউ সংখ্যা অনুযায়ী র‍্যাংক করে। আপনি সবসময় সত্যিকারের ইউটিউব ডেটা দেখছেন, আমাদের নিজস্ব মতামত নয়।' },
        { q: 'ডেটা কত ঘন ঘন আপডেট হয়?', a: 'চার্টগুলো নিয়মিত রিফ্রেশ হয় — ইউটিউব থেকে ডেটা আবার আনা হয় এবং প্রায় ১০ মিনিটের জন্য এজে ক্যাশ করা হয়, তাই আপনি যা দেখছেন তা প্রায় রিয়েল-টাইম। প্রতিটি পেজে দেখানো থাকে সেটা কখন সর্বশেষ আপডেট হয়েছিল।' },
        { q: 'আমি কি আমার দেশে কী ট্রেন্ডিং তা দেখতে পারি?', a: 'হ্যাঁ। আপনি বিশ্বব্যাপী বা নির্দিষ্ট কোনো দেশের ট্রেন্ডিং ভিডিও ব্রাউজ করতে পারেন — যুক্তরাষ্ট্র, যুক্তরাজ্য, ভারত, জাপান, দক্ষিণ কোরিয়া, ব্রাজিল, জার্মানি, ফ্রান্স, কানাডা, অস্ট্রেলিয়া, মেক্সিকো এবং রাশিয়া — এবং মিউজিক, গেমিং, স্পোর্টস, বিনোদন ও সংবাদের মতো ক্যাটাগরি দিয়ে ফিল্টার করতে পারেন।' },
        { q: 'ইউটিউব শর্টস কি অন্তর্ভুক্ত আছে?', a: 'ডিফল্টভাবে শর্ট ভিডিওগুলো লুকানো থাকে, যাতে আপনি ফুল-লেংথ কনটেন্ট দেখতে পান, কিন্তু আপনি যখন চান শর্টস সুইচ দিয়ে শর্টস চালু করতে পারেন।' },
        { q: 'YouTubeMost কি ইউটিউবের সাথে সংযুক্ত?', a: 'না। YouTubeMost একটি স্বাধীন টুল যা পাবলিক YouTube Data API ব্যবহার করে। সব ভিডিও, থাম্বনেইল এবং চ্যানেল ইউটিউবে তাদের নিজ নিজ মালিকের।' },
      ],
      channelLoading: 'চ্যানেল লোড হচ্ছে…',
      channelSubscribers: 'সাবস্ক্রাইবার',
      channelVideosLabel: 'ভিডিও',
      channelTotalViews: 'টোটাল ভিউ',
      channelVisit: 'ইউটিউবে দেখুন',
      channelFullPage: 'সম্পূর্ণ চ্যানেল পেজ',
      channelOnChart: (n) => `এখন চার্টে আছে (${n})`,
      channelTrackedVideos: (n) => `সবচেয়ে বেশি দেখা ট্র্যাক করা ভিডিও (${n})`,
    },
  },
  ur: {
    chart: (c) => {
      const reg = c.isWorld ? 'دنیا بھر' : c.regionName;
      const tag = c.hasCat ? ` · ${c.catName}` : '';
      const cat = c.hasCat ? `${c.catName} ` : '';
      return {
        title: `یوٹیوب پر سب سے زیادہ دیکھی جانے والی ویڈیوز · ${reg}${tag}${BRAND}`,
        description: `${c.isWorld ? 'دنیا بھر میں' : c.regionName + ' میں'} اس وقت یوٹیوب پر سب سے مقبول ${cat}ویڈیوز، ویوز کے مطابق ترتیب اور مسلسل اپ ڈیٹ۔`,
        h1: `یوٹیوب پر سب سے زیادہ دیکھی جانے والی ویڈیوز · ${reg}${tag}`,
        intro: `${c.isWorld ? 'دنیا بھر میں' : c.regionName + ' میں'} اس وقت سب سے مقبول ${cat}ویڈیوز۔ کسی بھی ویڈیو پر ٹیپ کر کے یہیں دیکھیں۔`,
      };
    },
    records: (c) => {
      const tag = c.hasCat ? ` · ${c.catName}` : '';
      return {
        title: `اب تک کی سب سے زیادہ دیکھی جانے والی یوٹیوب ویڈیوز${tag}${BRAND}`,
        description: `اب تک کی سب سے زیادہ دیکھی جانے والی یوٹیوب ویڈیوز، کل ویوز کے مطابق ترتیب۔`,
        h1: `اب تک کی سب سے زیادہ دیکھی جانے والی یوٹیوب ویڈیوز${tag}`,
        intro: `ہر ویڈیو کل ویوز کے مطابق ترتیب دی گئی — یوٹیوب کی تاریخ کی سب سے زیادہ دیکھی جانے والی ویڈیوز۔`,
      };
    },
    ui: {
      trendingTab: 'ٹرینڈنگ', allTimeTab: 'سب سے زیادہ دیکھی گئی',
      suggest: (n) => `YouTubeMost کو ${n} میں دیکھیں؟`, dismiss: 'نہیں شکریہ',
      tagline: 'سب سے زیادہ دیکھی جانے والی ویڈیوز دریافت کریں',
      languageAriaLabel: 'زبان',
      themeToLight: 'لائٹ موڈ میں جائیں',
      themeToDark: 'ڈارک موڈ میں جائیں',
      footerAbout: 'YouTubeMost یوٹیوب کی سب سے زیادہ دیکھی جانے والی اور ٹرینڈنگ ویڈیوز کو مسلسل اپڈیٹ کے ساتھ ٹریک کرتا ہے۔ دنیا بھر کے یا کسی مخصوص ملک کے ٹاپ چارٹس دیکھیں — امریکہ، برطانیہ، بھارت، جاپان، برازیل اور مزید — اور میوزک، گیمنگ، اسپورٹس، انٹرٹینمنٹ اور نیوز جیسی کیٹیگریز کے حساب سے فلٹر کریں۔ یہ دیکھنے کا ایک تیز اور بے جھنجھٹ طریقہ ہے کہ دنیا اس وقت کیا دیکھ رہی ہے۔',
      footerPoweredBy: 'بذریعہ',
      footerBuiltBy: 'تیار کردہ از',
      categoryLabel: 'کیٹیگری',
      regionLabel: 'ریجن',
      shortsOn: 'شارٹس: آن',
      shortsOff: 'شارٹس: آف',
      shortsOnTitle: 'شارٹس شامل ہیں — چھپانے کے لیے کلک کریں',
      shortsOffTitle: 'شارٹس چھپی ہوئی ہیں — شامل کرنے کے لیے کلک کریں',
      randomLabel: 'رینڈم',
      randomTitle: 'ایک رینڈم ویڈیو چلائیں (R)',
      shareLabel: 'شیئر',
      shareCopiedLabel: 'کاپی ہو گیا!',
      shareTitle: 'اس ویو کا لنک کاپی کریں',
      infoTitle: 'YouTubeMost کیسے کام کرتا ہے',
      infoBody: 'YouTubeMost یوٹیوب پر اس وقت سب سے زیادہ دیکھی جانے والی اور ٹرینڈنگ ویڈیوز دیکھنے کا ایک تیز اور بے جھنجھٹ طریقہ ہے۔ ہم ہر ملک اور کیٹیگری کے لیے یوٹیوب ڈیٹا API سے آفیشل "سب سے مقبول" چارٹس حاصل کرتے ہیں، انہیں ویوز کی تعداد کے مطابق ترتیب دیتے ہیں، اور آپ کو صفحہ چھوڑے بغیر کوئی بھی ویڈیو چلانے دیتے ہیں۔ یہاں نہ کوئی سفارشات ہیں، نہ آٹو پلے کا چکر، اور نہ کوئی الگورتھم آپ کے فیصلوں پر سوال اٹھاتا ہے — صرف وہی جو دنیا واقعی دیکھ رہی ہے، مسلسل اپڈیٹ کے ساتھ۔',
      faqTitle: 'اکثر پوچھے گئے سوالات',
      faq: [
        { q: 'ٹرینڈنگ ویڈیوز کی ترتیب کیسے طے ہوتی ہے؟', a: 'YouTubeMost ہر ملک اور کیٹیگری کے لیے آفیشل یوٹیوب ڈیٹا API (وہی "سب سے مقبول" چارٹ جو یوٹیوب خود استعمال کرتا ہے) سے سب سے مقبول ویڈیوز حاصل کرتا ہے، پھر انہیں ویوز کی تعداد کے مطابق ترتیب دیتا ہے۔ آپ ہمیشہ حقیقی یوٹیوب ڈیٹا دیکھ رہے ہوتے ہیں، ہماری اپنی رائے نہیں۔' },
        { q: 'ڈیٹا کتنی بار اپڈیٹ ہوتا ہے؟', a: 'چارٹس مسلسل ریفریش ہوتے ہیں — ڈیٹا یوٹیوب سے دوبارہ حاصل کیا جاتا ہے اور تقریباً 10 منٹ کے لیے ایج پر کیش کیا جاتا ہے، اس لیے جو آپ دیکھتے ہیں وہ حقیقی وقت کے قریب ہوتا ہے۔ ہر صفحہ دکھاتا ہے کہ اسے آخری بار کب اپڈیٹ کیا گیا تھا۔' },
        { q: 'کیا میں اپنے ملک میں ٹرینڈنگ دیکھ سکتا ہوں؟', a: 'جی ہاں۔ آپ دنیا بھر میں یا کسی مخصوص ملک کے لیے ٹرینڈنگ ویڈیوز دیکھ سکتے ہیں — امریکہ، برطانیہ، بھارت، جاپان، جنوبی کوریا، برازیل، جرمنی، فرانس، کینیڈا، آسٹریلیا، میکسیکو اور روس — اور میوزک، گیمنگ، اسپورٹس، انٹرٹینمنٹ اور نیوز جیسی کیٹیگریز کے حساب سے فلٹر کر سکتے ہیں۔' },
        { q: 'کیا یوٹیوب شارٹس بھی شامل ہیں؟', a: 'ڈیفالٹ کے طور پر مختصر ویڈیوز چھپی ہوئی ہوتی ہیں تاکہ آپ مکمل طوالت کا مواد دیکھیں، لیکن آپ کسی بھی وقت شارٹس سوئچ کے ذریعے شارٹس کو آن کر سکتے ہیں۔' },
        { q: 'کیا YouTubeMost کا یوٹیوب سے کوئی تعلق ہے؟', a: 'نہیں۔ YouTubeMost ایک آزاد ٹول ہے جو پبلک یوٹیوب ڈیٹا API استعمال کرتا ہے۔ تمام ویڈیوز، تھمبنیلز اور چینلز یوٹیوب پر اپنے اپنے مالکان کی ملکیت ہیں۔' },
      ],
      channelLoading: 'چینل لوڈ ہو رہا ہے…',
      channelSubscribers: 'سبسکرائبرز',
      channelVideosLabel: 'ویڈیوز',
      channelTotalViews: 'کل ویوز',
      channelVisit: 'یوٹیوب پر دیکھیں',
      channelFullPage: 'مکمل چینل صفحہ',
      channelOnChart: (n) => `اس وقت چارٹ پر (${n})`,
      channelTrackedVideos: (n) => `سب سے زیادہ دیکھی گئی ٹریک شدہ ویڈیوز (${n})`,
    },
  },
  id: {
    chart: (c) => {
      const reg = c.isWorld ? 'dunia' : c.regionName;
      const tag = c.hasCat ? ` · ${c.catName}` : '';
      const cat = c.hasCat ? `${c.catName} ` : '';
      return {
        title: `Video YouTube terpopuler · ${reg}${tag}${BRAND}`,
        description: `Video ${cat}YouTube paling banyak ditonton ${c.isWorld ? 'di dunia' : 'di ' + c.regionName} saat ini, diurutkan berdasarkan jumlah tontonan dan terus diperbarui.`,
        h1: `Video YouTube terpopuler · ${reg}${tag}`,
        intro: `Video ${cat}YouTube terpopuler ${c.isWorld ? 'di dunia' : 'di ' + c.regionName} saat ini. Ketuk video mana pun untuk menontonnya di sini.`,
      };
    },
    records: (c) => {
      const tag = c.hasCat ? ` · ${c.catName}` : '';
      return {
        title: `Video YouTube terpopuler sepanjang masa${tag}${BRAND}`,
        description: `Video YouTube yang paling banyak ditonton sepanjang masa, diurutkan berdasarkan total tontonan.`,
        h1: `Video YouTube terpopuler sepanjang masa${tag}`,
        intro: `Setiap video diurutkan berdasarkan total tontonannya — video paling banyak ditonton dalam sejarah YouTube.`,
      };
    },
    ui: {
      trendingTab: 'Sedang tren', allTimeTab: 'Terpopuler sepanjang masa',
      suggest: (n) => `Lihat YouTubeMost dalam ${n}?`, dismiss: 'Tidak',
      tagline: 'Temukan Video dengan Tontonan Terbanyak',
      languageAriaLabel: 'Bahasa',
      themeToLight: 'Beralih ke mode terang',
      themeToDark: 'Beralih ke mode gelap',
      footerAbout: 'YouTubeMost melacak video dengan tontonan terbanyak dan sedang tren di YouTube, diperbarui terus-menerus. Jelajahi chart teratas dari seluruh dunia atau per negara — Amerika Serikat, Inggris, India, Jepang, Brasil, dan lainnya — dan filter berdasarkan kategori seperti Musik, Gaming, Olahraga, Hiburan, dan Berita. Ini cara cepat dan bebas ribet untuk melihat apa yang sedang ditonton dunia saat ini.',
      footerPoweredBy: 'Didukung oleh',
      footerBuiltBy: 'Dibuat oleh',
      categoryLabel: 'Kategori',
      regionLabel: 'Wilayah',
      shortsOn: 'Shorts: Aktif',
      shortsOff: 'Shorts: Nonaktif',
      shortsOnTitle: 'Shorts ditampilkan — klik untuk menyembunyikannya',
      shortsOffTitle: 'Shorts disembunyikan — klik untuk menampilkannya',
      randomLabel: 'Acak',
      randomTitle: 'Putar video acak (R)',
      shareLabel: 'Bagikan',
      shareCopiedLabel: 'Tersalin!',
      shareTitle: 'Salin tautan ke tampilan ini',
      infoTitle: 'Cara kerja YouTubeMost',
      infoBody: 'YouTubeMost adalah cara cepat dan bebas ribet untuk melihat video dengan tontonan terbanyak dan sedang tren di YouTube saat ini. Kami membaca chart "paling populer" resmi dari YouTube Data API untuk setiap negara dan kategori, mengurutkannya berdasarkan jumlah tontonan, dan memungkinkan Anda memutar video apa pun tanpa harus meninggalkan halaman. Tidak ada rekomendasi, tidak ada jebakan autoplay, dan tidak ada algoritma yang menebak-nebak keinginan Anda — hanya apa yang benar-benar sedang ditonton dunia, diperbarui terus-menerus.',
      faqTitle: 'Pertanyaan yang sering diajukan',
      faq: [
        { q: 'Bagaimana video yang sedang tren diurutkan?', a: 'YouTubeMost mengambil video paling populer langsung dari YouTube Data API resmi (chart "paling populer" yang sama yang digunakan YouTube) untuk setiap negara dan kategori, lalu mengurutkannya berdasarkan jumlah tontonan. Anda selalu melihat data YouTube yang sebenarnya, bukan opini kami sendiri.' },
        { q: 'Seberapa sering data diperbarui?', a: 'Chart diperbarui terus-menerus — data diambil ulang dari YouTube dan disimpan sementara (cache) di edge selama sekitar 10 menit, jadi apa yang Anda lihat mendekati waktu nyata. Setiap halaman menunjukkan kapan terakhir diperbarui.' },
        { q: 'Bisakah saya melihat apa yang sedang tren di negara saya?', a: 'Bisa. Anda dapat menjelajahi video yang sedang tren di seluruh dunia atau di negara tertentu — Amerika Serikat, Inggris, India, Jepang, Korea Selatan, Brasil, Jerman, Prancis, Kanada, Australia, Meksiko, dan Rusia — dan filter berdasarkan kategori seperti Musik, Gaming, Olahraga, Hiburan, dan Berita.' },
        { q: 'Apakah YouTube Shorts termasuk?', a: 'Secara default, video pendek disembunyikan agar Anda melihat konten berdurasi penuh, tetapi Anda bisa mengaktifkan Shorts kapan saja lewat sakelar Shorts.' },
        { q: 'Apakah YouTubeMost berafiliasi dengan YouTube?', a: 'Tidak. YouTubeMost adalah alat independen yang menggunakan YouTube Data API publik. Semua video, thumbnail, dan channel adalah milik pemiliknya masing-masing di YouTube.' },
      ],
      channelLoading: 'Memuat channel…',
      channelSubscribers: 'subscriber',
      channelVideosLabel: 'video',
      channelTotalViews: 'total tontonan',
      channelVisit: 'Kunjungi di YouTube',
      channelFullPage: 'Halaman channel lengkap',
      channelOnChart: (n) => `Di chart saat ini (${n})`,
      channelTrackedVideos: (n) => `Video terlacak dengan tontonan terbanyak (${n})`,
    },
  },
  ja: {
    chart: (c) => {
      const reg = c.isWorld ? '世界' : c.regionName;
      const tag = c.hasCat ? ` · ${c.catName}` : '';
      const cat = c.hasCat ? c.catName : '';
      return {
        title: `最も人気の YouTube 動画 · ${reg}${tag}${BRAND}`,
        description: `${reg}で今最も再生されている${cat}YouTube 動画。再生回数順にランキングし、常に更新中。`,
        h1: `最も人気の YouTube 動画 · ${reg}${tag}`,
        intro: `${reg}で今最も人気の${cat}動画を再生回数順に。動画をタップするとここで再生できます。`,
      };
    },
    records: (c) => {
      const tag = c.hasCat ? ` · ${c.catName}` : '';
      const cat = c.hasCat ? c.catName : '';
      return {
        title: `史上最も再生された YouTube 動画${tag}${BRAND}`,
        description: `史上最も再生された${cat}YouTube 動画を、総再生回数順にランキング。`,
        h1: `史上最も再生された YouTube 動画${tag}`,
        intro: `各動画は総再生回数順に並んでいます — YouTube 史上最も再生された動画。`,
      };
    },
    ui: {
      trendingTab: '急上昇', allTimeTab: '歴代最多再生',
      suggest: (n) => `YouTubeMost を${n}で表示しますか？`, dismiss: '結構です',
      tagline: '最も再生されている動画を発見',
      languageAriaLabel: '言語',
      themeToLight: 'ライトモードに切り替え',
      themeToDark: 'ダークモードに切り替え',
      footerAbout: 'YouTubeMost は、YouTube で再生数の多い動画や急上昇動画を継続的に追跡しています。世界全体または国別 — アメリカ、イギリス、インド、日本、ブラジルなど — でトップチャートを閲覧でき、音楽、ゲーム、スポーツ、エンターテインメント、ニュースなどのカテゴリで絞り込むこともできます。今、世界中で見られている動画を、シンプルに素早く確認できます。',
      footerPoweredBy: '提供',
      footerBuiltBy: '開発',
      categoryLabel: 'カテゴリ',
      regionLabel: '地域',
      shortsOn: 'Shorts: オン',
      shortsOff: 'Shorts: オフ',
      shortsOnTitle: 'Shorts が表示されています — クリックで非表示にできます',
      shortsOffTitle: 'Shorts は非表示です — クリックで表示できます',
      randomLabel: 'ランダム',
      randomTitle: 'ランダムな動画を再生（R）',
      shareLabel: '共有',
      shareCopiedLabel: 'コピーしました！',
      shareTitle: 'この表示のリンクをコピー',
      infoTitle: 'YouTubeMost の仕組み',
      infoBody: 'YouTubeMost は、今 YouTube で最も再生されている動画や急上昇動画を、シンプルに素早く確認できるサービスです。各国・各カテゴリごとに YouTube Data API の公式「人気」チャートを取得し、再生数でランキング化して、ページを離れずにそのまま動画を再生できるようにしています。おすすめ表示や自動再生の連鎖、アルゴリズムによる先読みはありません — 世界が実際に見ているものを、継続的に更新してお届けします。',
      faqTitle: 'よくある質問',
      faq: [
        { q: '急上昇動画はどのように順位付けされていますか？', a: 'YouTubeMost は、各国・各カテゴリについて YouTube Data API（YouTube が使用しているのと同じ「人気」チャート）から直接動画を取得し、再生数でランキング化しています。表示されているのは常に実際の YouTube データであり、当サイト独自の判断ではありません。' },
        { q: 'データはどのくらいの頻度で更新されますか？', a: 'チャートは継続的に更新されています — データは YouTube から再取得され、エッジで約10分間キャッシュされるため、ほぼリアルタイムの内容が表示されます。各ページには最終更新時刻が表示されます。' },
        { q: '自分の国の急上昇動画を見ることはできますか？', a: 'はい。世界全体、または特定の国 — アメリカ、イギリス、インド、日本、韓国、ブラジル、ドイツ、フランス、カナダ、オーストラリア、メキシコ、ロシア — の急上昇動画を閲覧でき、音楽、ゲーム、スポーツ、エンターテインメント、ニュースなどのカテゴリで絞り込むこともできます。' },
        { q: 'YouTube Shorts は含まれていますか？', a: 'デフォルトでは短編動画は非表示になっており、通常の長さの動画のみが表示されますが、Shorts スイッチでいつでも切り替えることができます。' },
        { q: 'YouTubeMost は YouTube と提携していますか？', a: 'いいえ。YouTubeMost は、公開されている YouTube Data API を利用した独立したツールです。すべての動画、サムネイル、チャンネルは、YouTube 上のそれぞれの所有者に帰属します。' },
      ],
      channelLoading: 'チャンネルを読み込み中…',
      channelSubscribers: '登録者数',
      channelVideosLabel: '本の動画',
      channelTotalViews: '総再生回数',
      channelVisit: 'YouTube で見る',
      channelFullPage: 'チャンネルページを見る',
      channelOnChart: (n) => `現在チャート入り中（${n}）`,
      channelTrackedVideos: (n) => `再生数の多い追跡動画（${n}）`,
    },
  },
  ru: {
    chart: (c) => {
      const reg = c.isWorld ? 'в мире' : c.regionName;
      const tag = c.hasCat ? ` · ${c.catName}` : '';
      return {
        title: `Самые популярные видео на YouTube · ${reg}${tag}${BRAND}`,
        description: `Самые просматриваемые видео на YouTube прямо сейчас${c.isWorld ? ' в мире' : ' — ' + c.regionName}, отсортированные по числу просмотров и постоянно обновляемые.`,
        h1: `Самые популярные видео на YouTube · ${reg}${tag}`,
        intro: `Самые популярные видео на YouTube прямо сейчас${c.isWorld ? ' в мире' : ' — ' + c.regionName}. Нажмите на любое видео, чтобы посмотреть его здесь.`,
      };
    },
    records: (c) => {
      const tag = c.hasCat ? ` · ${c.catName}` : '';
      return {
        title: `Самые просматриваемые видео на YouTube за всё время${tag}${BRAND}`,
        description: `Самые просматриваемые видео на YouTube за всё время, отсортированные по общему числу просмотров.`,
        h1: `Самые просматриваемые видео на YouTube за всё время${tag}`,
        intro: `Каждое видео отсортировано по общему числу просмотров — самые просматриваемые видео за всю историю YouTube.`,
      };
    },
    ui: {
      trendingTab: 'В тренде', allTimeTab: 'Самые просматриваемые',
      suggest: (n) => `Открыть YouTubeMost на ${n}?`, dismiss: 'Нет, спасибо',
      tagline: 'Самые просматриваемые видео',
      languageAriaLabel: 'Язык',
      themeToLight: 'Включить светлую тему',
      themeToDark: 'Включить тёмную тему',
      footerAbout: 'YouTubeMost отслеживает самые просматриваемые и популярные видео на YouTube в режиме реального времени. Смотрите топ-чарты по всему миру или по странам — США, Великобритания, Индия, Япония, Бразилия и другие — и фильтруйте по категориям: Музыка, Игры, Спорт, Развлечения и Новости. Это быстрый и лаконичный способ увидеть, что сейчас смотрит весь мир.',
      footerPoweredBy: 'Работает на',
      footerBuiltBy: 'Создано',
      categoryLabel: 'Категория',
      regionLabel: 'Регион',
      shortsOn: 'Shorts: Вкл',
      shortsOff: 'Shorts: Выкл',
      shortsOnTitle: 'Shorts показываются — нажмите, чтобы скрыть их',
      shortsOffTitle: 'Shorts скрыты — нажмите, чтобы показать их',
      randomLabel: 'Случайное',
      randomTitle: 'Включить случайное видео (R)',
      shareLabel: 'Поделиться',
      shareCopiedLabel: 'Скопировано!',
      shareTitle: 'Скопировать ссылку на этот вид',
      infoTitle: 'Как работает YouTubeMost',
      infoBody: 'YouTubeMost — это быстрый и лаконичный способ увидеть самые просматриваемые и популярные видео на YouTube прямо сейчас. Мы получаем официальные чарты «самое популярное» из YouTube Data API для каждой страны и категории, ранжируем их по количеству просмотров и позволяем воспроизвести любое видео, не покидая страницу. Никаких рекомендаций, никаких бесконечных автовоспроизведений и никакого алгоритма, решающего за вас — только то, что мир действительно смотрит, в режиме реального времени.',
      faqTitle: 'Часто задаваемые вопросы',
      faq: [
        { q: 'Как ранжируются популярные видео?', a: 'YouTubeMost получает самые популярные видео напрямую из официального YouTube Data API (тот же чарт «самое популярное», который использует сам YouTube) для каждой страны и категории, а затем ранжирует их по количеству просмотров. Вы всегда видите реальные данные YouTube, а не наше собственное мнение.' },
        { q: 'Как часто обновляются данные?', a: 'Чарты обновляются непрерывно — данные повторно загружаются с YouTube и кэшируются на edge-серверах примерно на 10 минут, поэтому то, что вы видите, максимально близко к реальному времени. На каждой странице указано время последнего обновления.' },
        { q: 'Могу ли я увидеть, что в тренде в моей стране?', a: 'Да. Вы можете просматривать популярные видео по всему миру или для конкретной страны — США, Великобритании, Индии, Японии, Южной Кореи, Бразилии, Германии, Франции, Канады, Австралии, Мексики и России — и фильтровать по категориям, таким как Музыка, Игры, Спорт, Развлечения и Новости.' },
        { q: 'Включены ли YouTube Shorts?', a: 'По умолчанию короткие видео скрыты, чтобы вы видели контент полной длины, но вы можете включить Shorts в любой момент с помощью переключателя Shorts.' },
        { q: 'YouTubeMost связан с YouTube?', a: 'Нет. YouTubeMost — независимый инструмент, использующий публичный YouTube Data API. Все видео, миниатюры и каналы принадлежат их соответствующим владельцам на YouTube.' },
      ],
      channelLoading: 'Загрузка канала…',
      channelSubscribers: 'подписчиков',
      channelVideosLabel: 'видео',
      channelTotalViews: 'всего просмотров',
      channelVisit: 'Открыть на YouTube',
      channelFullPage: 'Страница канала',
      channelOnChart: (n) => `Сейчас в чарте (${n})`,
      channelTrackedVideos: (n) => `Самые просматриваемые отслеживаемые видео (${n})`,
    },
  },
};

/** English UI labels (the English SEO copy is produced by pages.ts directly). */
export const EN_UI: UiPack = {
  trendingTab: 'Trending now',
  allTimeTab: 'Most viewed of all time',
  suggest: (n: string) => `View YouTubeMost in ${n}?`,
  dismiss: 'No thanks',

  tagline: 'Discover the Most Viewed Videos',
  languageAriaLabel: 'Language',
  themeToLight: 'Switch to light mode',
  themeToDark: 'Switch to dark mode',
  footerAbout:
    'YouTubeMost tracks the most viewed and trending videos on YouTube, updated continuously. Browse the top ' +
    'charts worldwide or by country — United States, United Kingdom, India, Japan, Brazil and more — and filter ' +
    'by category like Music, Gaming, Sports, Entertainment and News. It’s a fast, clutter-free way to see ' +
    'what the world is watching right now.',
  footerPoweredBy: 'Powered by',
  footerBuiltBy: 'Built by',

  categoryLabel: 'Category',
  regionLabel: 'Region',
  shortsOn: 'Shorts: On',
  shortsOff: 'Shorts: Off',
  shortsOnTitle: 'Shorts are included — click to hide them',
  shortsOffTitle: 'Shorts are hidden — click to include them',
  randomLabel: 'Random',
  randomTitle: 'Play a random video (R)',
  shareLabel: 'Share',
  shareCopiedLabel: 'Copied!',
  shareTitle: 'Copy a link to this view',

  infoTitle: 'How YouTubeMost works',
  infoBody:
    'YouTubeMost is a fast, clutter-free way to see the most-viewed and trending videos on YouTube right now. ' +
    'We read the official “most popular” charts from the YouTube Data API for each country and category, ' +
    'rank them by view count, and let you play any video without leaving the page. There are no recommendations, ' +
    'no autoplay rabbit holes, and no algorithm second-guessing you — just what the world is actually ' +
    'watching, updated continuously.',
  faqTitle: 'Frequently asked questions',
  faq: [
    {
      q: 'How are the trending videos ranked?',
      a: 'YouTubeMost pulls the most popular videos directly from the official YouTube Data API (the same "most popular" chart YouTube uses) for each country and category, then ranks them by view count. You are always seeing real YouTube data, not our own opinion.',
    },
    {
      q: 'How often is the data updated?',
      a: 'The charts refresh continuously — the data is re-fetched from YouTube and cached at the edge for about 10 minutes, so what you see is close to real time. Each page shows when it was last updated.',
    },
    {
      q: 'Can I see what is trending in my country?',
      a: 'Yes. You can browse trending videos worldwide or for a specific country — the United States, United Kingdom, India, Japan, South Korea, Brazil, Germany, France, Canada, Australia, Mexico and Russia — and filter by category such as Music, Gaming, Sports, Entertainment and News.',
    },
    {
      q: 'Are YouTube Shorts included?',
      a: 'By default short videos are hidden so you see full-length content, but you can toggle Shorts on at any time using the Shorts switch.',
    },
    {
      q: 'Is YouTubeMost affiliated with YouTube?',
      a: 'No. YouTubeMost is an independent tool that uses the public YouTube Data API. All videos, thumbnails and channels belong to their respective owners on YouTube.',
    },
  ],

  channelLoading: 'Loading channel…',
  channelSubscribers: 'subscribers',
  channelVideosLabel: 'videos',
  channelTotalViews: 'total views',
  channelVisit: 'Visit on YouTube',
  channelFullPage: 'Full channel page',
  channelOnChart: (n: number) => `On the chart right now (${n})`,
  channelTrackedVideos: (n: number) => `Most-viewed tracked videos (${n})`,
};

export function getUi(lang: Lang) {
  return lang === 'en' ? EN_UI : PACKS[lang].ui;
}

/** Localized SEO strings for a page. `en` is handled by the caller (pages.ts). */
export function localizedSeo(lang: Exclude<Lang, 'en'>, kind: 'chart' | 'records', ctx: SeoCtx): SeoStrings {
  return kind === 'records' ? PACKS[lang].records(ctx) : PACKS[lang].chart(ctx);
}
